import React, { useEffect, useState } from "react";
import styled from "styled-components";
import * as d3 from "d3";
import { InputLabel, MenuItem, FormControl, Select } from "@mui/material";
import { DownIcon } from "common/SelectInput";

const InputLayout = styled.div`
  align-items: middle;
  & .form-line {
  }
  & .form-line + .input-group-addon {
    padding-right: 10px;
  }
  .reset,
  .spin-down,
  .spin-up {
    cursor: pointer;
  }
  .form-line.input-line {
    text-align: right;
  }
  .number-input {
    width: 100%;
    margin-left: 5px;
    padding-right: 5px;
  }
`;

const Label = ({ label, subLabel }) => {
    if (label === null) {
      return null;
    }
    if (label === "noLabel"){
      return <span className="label-wrapper input-group-addon padding-0"></span>
    }
    if (label && subLabel) {
      return (
        <span className="label-wrapper input-group-addon padding-0">
          {label}:<br />
          {subLabel}
        </span>
      );
    }
    return (
      <span className="label-wrapper input-group-addon padding-0">{label}:</span>
    );
  };

  const Units = ({ units, selectedUnit, setSelectedUnit }) => {
    if (units === null) {
      return null;
    }
  
    if (typeof units === "string") {
      return <span className="input-group-addon">{units}&emsp;</span>;
    }
  
    return (
      <span className="input-group-addon">
        <Select
          name="Unit"
          value={selectedUnit}
          onChange={({target})=> setSelectedUnit(target.value)}
          title="Unit"
          IconComponent={DownIcon}
          sx={{
            fontSize: "12px",
            fontFamily: "AllianceNo2-SemiBold",
            width: "100%",
            boxShadow: "none",
            ".MuiOutlinedInput-notchedOutline": { border: 0 },
            "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
              border: 0,
            },
            "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
              {
                border: 0,
              },
            ".MuiInputBase-input": {
              padding: "4px 14px",
              color: "var(--Text-Primary)",
              borderRadius: "0px",
            },
          }}
        >{units.map(({ value, label }) => (
          <MenuItem value={value} key={`${value}-option`} sx={{ fontSize: "12px", fontFamily: "AllianceNo2-SemiBold !important" }}>
            {label}
          </MenuItem>
        ))}</Select>
      </span>
    );
  };

const formatInputDefault = (d) => d3.format(".3f")(d);

const NumberInputWithSpinner = ({
  initialValue,
  min = -Infinity,
  max = Infinity,
  step = 1,
  className,
  name,
  label,
  subLabel,
  units = null,
  onChange,
  onReset,
  formatInput = formatInputDefault,
  hideSpinner = false,
  hideReset = false,
  selectedUnit,
  setSelectedUnit,
  defaultUnit,
  onFocus
}) => {
  const [value, setValue] = useState(initialValue);

  useEffect(()=>{
   setValue(initialValue);
  },[initialValue])

  const handleInputChange = ({target}) => {
    let newValue = target.value;
    if (newValue === "") {
      setValue("");
    } else {
      newValue = Number(newValue);
      if (!isNaN(newValue) && newValue >= min && newValue <= max) {
        setValue(newValue);
        if (onChange) {
          onChange(newValue);
        }
      }
    }
  };

  const increment = () => {
    
    const newValue = value === "" ? step : parseFloat(value) + step;
    const newValueWithin = newValue <= max ? newValue : max;
    const newValueFormat = parseFloat(formatInput(newValueWithin));
    setValue(newValueFormat);
    if (onChange) {
      onChange(newValueFormat);
    }
  };

  const decrement = () => {
    const newValue = value === "" ? min : value - step;
    const newValueWithin = newValue >= min ? newValue : min;
    const newValueFormat = parseFloat(formatInput(newValueWithin));
    setValue(newValueFormat);
    if (onChange) {
      onChange(newValueFormat);
    }
  };

  const handleReset = () => {
    if (onReset) {
      onReset();
    } else {
      if (onChange) {
        onChange(initialValue);
      }
      setValue(initialValue);
    }
  };

  const handleBlur = () => {
    let newValue = Number(value);
    if (isNaN(newValue)) newValue = min;
    newValue = Math.max(Math.min(newValue, max), min);
    setValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  return (
    <InputLayout
      className={`input-group spinner margin-0 ${className}`}
      data-trigger="spinner"
    >
      <Label label={label} subLabel={subLabel} />
      <div className="form-line input-line">
        <input
          name={name}
          className="form-control align-right min-width-45"
          type="number"
          value={value}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onFocus={onFocus}
          className="number-input"
          min={min}
          max={max}
        />
      </div>
      <Units
        units={units}
        selectedUnit={selectedUnit}
        setSelectedUnit={setSelectedUnit}
      />
      {hideSpinner ? null : (
        <div className="input-group-addon">
          <div onClick={increment} disabled={value <= min} className="spin-up">
            <i className="glyphicon glyphicon-chevron-up" />
          </div>
          <div
            onClick={decrement}
            disabled={value >= max}
            className="spin-down"
          >
            <i className="glyphicon glyphicon-chevron-down" />
          </div>
        </div>
      )}
      {hideReset ? null : (
        <div className="input-group-addon reset">
          <div onClick={handleReset} title="Reset Default">
            <i className="material-icons">backspace</i>
          </div>
        </div>
      )}
    </InputLayout>
  );
};

export default NumberInputWithSpinner;